import React from "react";
import {
  OrderPayBreakDownWithAllocation,
  Shipment,
} from "@api/graphql/generated/generated-types";
import { Button, Label, Tooltip } from "@src/common/components";
import { Loading } from "@src/common/components/Loading";
import DollarInput from "@src/common/components/Input/DollarInput";
import {
  ArrowUturnLeftIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { useOrderPayBreakdownLogic } from "../hooks/useOrderPayBreakdownLogic";
import { DriverAllocationList } from "./components/DriverAllocationList";

type Props = {
  selectedShipment: Shipment | null | undefined;
  setDriverPayTotal: React.Dispatch<React.SetStateAction<number | undefined>>;
  driverPayTotal: number | undefined;
};

function OrderPayBreakDown({
  selectedShipment,
  setDriverPayTotal,
  driverPayTotal,
}: Props) {
  const {
    allocationsData,
    allocationsLoading,
    isDriverCompensationOverride,
    updateDriverPayOverrideMutationLoading,
    handleSave,
    handleUndoOverride,
  } = useOrderPayBreakdownLogic({
    selectedShipment,
    setDriverPayTotal,
    driverPayTotal,
  });

  if (allocationsLoading) {
    return <Loading loading={true} className="p-3 h-full overflow-auto" />;
  }

  const amountAllocations = allocationsData?.allocations.filter(
    (x) => x.allocationAmount
  );
  const percentAllocations = allocationsData?.allocations.filter(
    (x) => !x.allocationAmount
  );

  return (
    <div className="shadow-md dark:bg-gray-600 p-4 pt-2 rounded-md">
      {(amountAllocations && amountAllocations.length > 0) && (
        <div className={"border dark:border-gray-500 mb-2 p-2 rounded-md"}>
          <Label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Flat Fee Allocations</Label>
          <DriverAllocationList
            allocations={amountAllocations as OrderPayBreakDownWithAllocation[]}
          />
        </div>
      )}

      <div className={"border dark:border-gray-500 mb-2 p-2 rounded-md"}>
        <Label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Percentage Allocations</Label>

        <DriverAllocationList
          allocations={percentAllocations as OrderPayBreakDownWithAllocation[]}
        />

        <TotalSection
          driverPayTotal={driverPayTotal}
          setDriverPayTotal={setDriverPayTotal}
          isLoading={updateDriverPayOverrideMutationLoading}
        />

        <ControlButtons
          isDriverCompensationOverride={isDriverCompensationOverride}
          handleUndoOverride={handleUndoOverride}
          handleSave={handleSave}
          isDisabled={false}
          isLoading={updateDriverPayOverrideMutationLoading}
        />
      </div>
    </div>
  );
}

function TotalSection({
  driverPayTotal,
  setDriverPayTotal,
  isLoading,
}: {
  driverPayTotal: number | undefined;
  setDriverPayTotal: React.Dispatch<React.SetStateAction<number | undefined>>;
  isLoading: boolean;
}) {
  return (
    <div className="flex flex-col pt-2">
      <div className="flex justify-between">
        <Label className="font-bold dark:text-gray-200">Total</Label>
        <Tooltip
          content={"Total amount reflects summation of percentage based allocations."}
          placement="top"
        >
          <QuestionMarkCircleIcon className="h-6 w-6 cursor-pointer dark:text-gray-400" />
        </Tooltip>
      </div>
      <Loading loading={isLoading}>
        <DollarInput
          dollar={Number(driverPayTotal) || 0}
          setDollar={(value) => setDriverPayTotal(value)}
          onChange={(event) => {
            const value = event.target.value;
            setDriverPayTotal(Number(value));
          }}
        />
      </Loading>
    </div>
  );
}

function ControlButtons({
  isDriverCompensationOverride,
  handleUndoOverride,
  handleSave,
  isDisabled,
  isLoading,
}: {
  isDriverCompensationOverride: boolean;
  handleUndoOverride: () => void;
  handleSave: () => void;
  isDisabled: boolean;
  isLoading: boolean;
}) {
  return (
    <div className="flex justify-between mt-4">
      {isDriverCompensationOverride ? (
        <span
          onClick={handleUndoOverride}
          className="flex truncate items-center text-xs cursor-pointer text-blue-500 dark:text-blue-400 hover:underline"
        >
          <ArrowUturnLeftIcon className="h-4 w-4 mr-1" />
          Undo Override
        </span>
      ) : (
        <div></div>
      )}
      <Button
        disabled={isDisabled || isLoading}
        size="xs"
        onClick={handleSave}
        isProcessing={isLoading}
      >
        Save Changes
      </Button>
    </div>
  );
}

export default OrderPayBreakDown;
